import {
  analisarAncoragensBasicas,
  comprimentoDaCorda,
  definicaoDaFuncao,
  folgaDoTrecho,
  FOLGA_MAXIMA_TRECHO_M,
  FUNCOES_CORDA,
  pontasSoltas,
  pontosDeAncoragem,
  type AnaliseAncoragemBasica,
  type AtualizacoesCorda,
  type CenaTabletop,
  type CordaTabletop,
  type FuncaoCorda,
  type ReferenciaPonto,
  type SentidoCorda,
} from '../../dominio/tabletop'
import type { DesenhoCorda } from './useCordasTabletop'

const ROTULOS_SENTIDO: Readonly<Record<SentidoCorda | 'nenhum', string>> = {
  nenhum: 'Sem seta',
  paraFim: 'Seta para o fim',
  paraInicio: 'Seta para o início',
}

function nomeDoPonto(cena: CenaTabletop, referencia: ReferenciaPonto): string {
  const objeto = cena.objetos.find((o) => o.id === referencia.idObjeto)
  if (!objeto) return `${referencia.idObjeto} · ${referencia.idPonto}`
  const ponto = pontosDeAncoragem(objeto).find((p) => p.id === referencia.idPonto)
  return `${objeto.nome} · ${ponto?.nome ?? `${referencia.idPonto} (não existe mais)`}`
}

const formatarMetros = (valor: number) => `${valor.toFixed(2).replace('.', ',')} m`

function Amostra({ funcao }: { funcao: FuncaoCorda }) {
  return <span className="ponto-cor corda-amostra" aria-hidden="true" style={{ background: definicaoDaFuncao(funcao).cor }} />
}

/** O nome que identifica a corda na lista: o rótulo do instrutor, ou a função. */
export const nomeDaCorda = (corda: CordaTabletop) => corda.rotulo ?? definicaoDaFuncao(corda.funcao).rotulo

export interface PropsPainelCordas {
  cena: CenaTabletop
  desenho: DesenhoCorda | null
  cordaSelecionada: CordaTabletop | null
  aoIniciar: (funcao: FuncaoCorda) => void
  aoTrocarFuncaoDoDesenho: (funcao: FuncaoCorda) => void
  aoDesfazerPonto: () => void
  aoConcluir: () => void
  aoCancelar: () => void
  aoSelecionarCorda: (id: string | null) => void
  aoAtualizarCorda: (id: string, alteracoes: AtualizacoesCorda) => void
  aoInverterSentido: (id: string) => void
  /** Folga de um trecho, em metros: deixa o trecho frouxo, pendurado. */
  aoDefinirFolga?: (id: string, indiceTrecho: number, folgaM: number) => void
  aoRemoverCorda: (id: string) => void
}

/**
 * Painel das cordas: escolher a função e desenhar clicando nos pontos de
 * ancoragem, ver a lista e editar a corda selecionada. A cor sempre vem com o
 * nome da função escrito — nunca só a cor.
 */
export function PainelCordas(props: PropsPainelCordas) {
  const { cena, desenho, cordaSelecionada } = props
  const cordas = cena.cordas ?? []
  const pontos = desenho?.percurso.length ?? 0

  return (
    <div className="grupo-controle painel-cordas">
      <span className="titulo-controle">Cordas</span>

      {desenho ? (
        <div className="corda-desenho" role="group" aria-label="Desenhando corda">
          {(() => {
            const ehFita = desenho.funcao === 'fita'
            const podeConcluir = ehFita ? pontos >= 1 : pontos >= 2
            const rotuloConcluir =
              ehFita && pontos === 1
                ? 'Concluir fita simples (ponta livre)'
                : ehFita && pontos === 2
                  ? 'Concluir ancoragem em V'
                  : ehFita && pontos === 3
                    ? 'Concluir ancoragem em W'
                    : ehFita && pontos >= 4
                      ? 'Concluir ancoragem em M'
                      : 'Concluir corda'

            const dicaTexto =
              pontos === 0
                ? 'Toque nos pontos de ancoragem, em sequência. Cada ponto aparece como uma bolinha na cena.'
                : ehFita
                  ? pontos === 1
                    ? '1 ponto: conclua para criar fita simples com ponta livre ou toque em mais pontos para equalização em V, W ou M.'
                    : `${pontos} pontos: ancoragem equalizada em ${pontos === 2 ? 'V' : pontos === 3 ? 'W' : 'M'}. Conclua ou continue tocando.`
                  : `${pontos} ponto${pontos > 1 ? 's' : ''} escolhido${pontos > 1 ? 's' : ''}. ${
                      pontos < 2 ? 'Escolha mais um ponto para formar a corda.' : 'Conclua (Enter) ou continue tocando.'
                    }`

            return (
              <>
                <p className="dica-tabletop" aria-live="polite">
                  {dicaTexto}
                </p>
                <FuncaoSeletor valor={desenho.funcao} aoEscolher={props.aoTrocarFuncaoDoDesenho} nome="Função da corda em desenho" />
                {pontos > 0 && (
                  <ol className="lista-pontos-ancoragem corda-percurso">
                    {desenho.percurso.map((ref, i) => (
                      <li key={i}>{nomeDoPonto(cena, ref)}</li>
                    ))}
                  </ol>
                )}
                <div className="acoes-linha">
                  <button type="button" className="btn-acao-cad btn-acao-cad--destaque" disabled={!podeConcluir} onClick={props.aoConcluir}>
                    {rotuloConcluir}
                  </button>
                  <button type="button" className="btn-acao-cad btn-acao-cad--secundario" disabled={pontos === 0} onClick={props.aoDesfazerPonto}>
                    Desfazer ponto
                  </button>
                  <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={props.aoCancelar}>
                    Cancelar (Esc)
                  </button>
                </div>
              </>
            )
          })()}
        </div>
      ) : (
        <>
          <p className="dica-tabletop">
            Escolha a função da corda. Os pontos de ancoragem de todos os objetos acendem e você toca neles em sequência.
          </p>
          <div className="funcoes-corda" role="group" aria-label="Desenhar corda por função">
            {FUNCOES_CORDA.map((funcao) => (
              <button
                key={funcao.id}
                type="button"
                className="btn-acao-cad btn-acao-cad--secundario"
                title={funcao.descricao}
                onClick={() => props.aoIniciar(funcao.id)}
              >
                <Amostra funcao={funcao.id} />
                {funcao.rotulo}
              </button>
            ))}
          </div>
          {FUNCOES_CORDA.some((f) => !f.corConfirmada) && (
            <p className="dica">As cores de segurança e de linha da vítima ainda esperam a confirmação do instrutor.</p>
          )}
        </>
      )}

      {cordaSelecionada && !desenho && <InspetorCorda {...props} corda={cordaSelecionada} />}

      <p className="dica" data-testid="legenda-setas">
        Setas na cena: nas cordas com sentido (puxada), uma seta a cada trecho mostra para onde a corda traciona; nas demais, as setas
        saem de cada ponta para dentro da corda. Desligue em "Setas de tração".
      </p>

      <details open={cordas.length > 0 && !cordaSelecionada}>
        <summary>Cordas na cena ({cordas.length})</summary>
        {cordas.length === 0 ? (
          <p className="dica">Nenhuma corda ainda.</p>
        ) : (
          <ul className="lista-pontos-ancoragem lista-cordas">
            {cordas.map((corda) => (
              <li key={corda.id}>
                <button
                  type="button"
                  className="btn-link-corda"
                  aria-pressed={corda.id === cordaSelecionada?.id}
                  onClick={() => props.aoSelecionarCorda(corda.id === cordaSelecionada?.id ? null : corda.id)}
                >
                  <Amostra funcao={corda.funcao} />
                  {nomeDaCorda(corda)} <span className="dica">({formatarMetros(comprimentoDaCorda(cena, corda))})</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </details>
    </div>
  )
}

function FuncaoSeletor({
  valor,
  aoEscolher,
  nome,
}: {
  valor: FuncaoCorda
  aoEscolher: (funcao: FuncaoCorda) => void
  nome: string
}) {
  return (
    <div className="funcoes-corda" role="radiogroup" aria-label={nome}>
      {FUNCOES_CORDA.map((funcao) => (
        <button
          key={funcao.id}
          type="button"
          role="radio"
          aria-checked={funcao.id === valor}
          className={`btn-acao-cad ${funcao.id === valor ? 'btn-acao-cad--destaque' : 'btn-acao-cad--secundario'}`}
          title={funcao.descricao}
          onClick={() => aoEscolher(funcao.id)}
        >
          <Amostra funcao={funcao.id} />
          {funcao.rotulo}
        </button>
      ))}
    </div>
  )
}

const textoDaQueda = (queda: number, balanco: number) =>
  queda < 0.005
    ? 'Ninguém desce.'
    : `Desce ${formatarMetros(queda)} até parar pendurado${balanco >= 0.1 ? ` e balança: fica a ${formatarMetros(balanco)} da vertical do ponto que segura` : ''}.`

/**
 * O estudo da ancoragem básica, quando a corda selecionada é a corda de uma
 * (ou o fusível dela): os elementos e o que acontece se cada um falhar.
 */
function EstudoAncoragemBasica({ cena, corda }: { cena: CenaTabletop; corda: CordaTabletop }) {
  const analise: AnaliseAncoragemBasica | undefined = analisarAncoragensBasicas(cena).find(
    (a) => a.idCorda === corda.id || a.fusivel?.idCorda === corda.id,
  )
  if (!analise) return null
  // o nome do objeto só entra quando diz algo além do papel ("Ponto primário" chamado "Ponto primário" não repete)
  const comNome = (ref: ReferenciaPonto, papel: string, texto: string) => {
    const n = cena.objetos.find((o) => o.id === ref.idObjeto)?.nome ?? ref.idObjeto
    return n.trim().toLowerCase() === papel.toLowerCase() ? texto.charAt(0).toUpperCase() + texto.slice(1) : `${n}: ${texto}`
  }
  return (
    <section className="estudo-ancoragem" aria-label="Estudo da ancoragem básica">
      <h4>Estudo da ancoragem básica</h4>
      <dl className="estudo-ancoragem__elementos">
        <dt>Ponto secundário</dt>
        <dd>{comNome(analise.secundario, 'Ponto secundário', 'a reserva, se o primário falhar.')}</dd>
        <dt>Catenária</dt>
        <dd>
          {formatarMetros(analise.comprimentoCatenariaM)} de corda entre os dois pontos, com {formatarMetros(analise.folgaCatenariaM)} de folga.
        </dd>
        <dt>Ponto primário</dt>
        <dd>{comNome(analise.primario, 'Ponto primário', 'onde a corda faz o nó e a carga fica no uso normal.')}</dd>
        <dt>Folga de tensão</dt>
        <dd>{analise.fusivel ? `${formatarMetros(analise.fusivel.folgaDeTensaoM)} no laço entre o nó do primário e o nó do fusível.` : 'Não há: sem fusível, a carga fica direto no nó do primário.'}</dd>
        <dt>Fusível</dt>
        <dd>{analise.fusivel ? 'Cordelete do primário até a corda, abaixo do nó: segura a carga no uso normal.' : 'Não há fusível na mesa.'}</dd>
      </dl>
      {analise.catenariaEsticada && (
        <p className="aviso-corda" role="note">
          A catenária está sem folga: o secundário divide a carga com o primário em vez de ficar de reserva. Dê folga ao primeiro trecho.
        </p>
      )}
      <ul className="estudo-ancoragem__cenarios" aria-label="Se algo falhar">
        {analise.cenarios.map((c) => (
          <li key={c.id} data-cenario={c.id}>
            <strong>{c.titulo}.</strong> {c.quemSegura} {textoDaQueda(c.quedaM, c.balancoM)}
          </li>
        ))}
      </ul>
      <p className="dica">
        Modelo didático: conta só o comprimento de corda (trechos retos mais as folgas), sem elasticidade nem força de impacto. Mais folga na catenária
        é mais queda se o primário falhar.
      </p>
    </section>
  )
}

function InspetorCorda(props: PropsPainelCordas & { corda: CordaTabletop }) {
  const { cena, corda } = props
  const soltas = pontasSoltas(cena, corda)
  const definicao = definicaoDaFuncao(corda.funcao)
  return (
    <fieldset className="inspetor-vitima inspetor-corda">
      <legend>Corda selecionada</legend>
      <p className="corda-resumo">
        <Amostra funcao={corda.funcao} />
        <strong>{definicao.rotulo}</strong> · {formatarMetros(comprimentoDaCorda(cena, corda))}
        <span className="dica">{corda.folgas?.length ? ' (aproximado: trechos retos mais as folgas)' : ' (aproximado, em linha reta)'}</span>
      </p>

      <label className="campo">
        <span>função</span>
        <select
          value={corda.funcao}
          aria-label="Função da corda"
          onChange={(e) => props.aoAtualizarCorda(corda.id, { funcao: e.target.value as FuncaoCorda })}
        >
          {FUNCOES_CORDA.map((f) => (
            <option key={f.id} value={f.id}>
              {f.rotulo}
            </option>
          ))}
        </select>
      </label>

      <label className="campo">
        <span>rótulo</span>
        <input
          value={corda.rotulo ?? ''}
          placeholder="opcional"
          aria-label="Rótulo da corda"
          onChange={(e) => props.aoAtualizarCorda(corda.id, { rotulo: e.target.value })}
        />
      </label>

      <div className="campo">
        <span>sentido de puxada</span>
        <select
          value={corda.sentido ?? 'nenhum'}
          aria-label="Sentido de puxada"
          onChange={(e) =>
            props.aoAtualizarCorda(corda.id, { sentido: e.target.value === 'nenhum' ? null : (e.target.value as SentidoCorda) })
          }
        >
          {(Object.keys(ROTULOS_SENTIDO) as Array<SentidoCorda | 'nenhum'>).map((chave) => (
            <option key={chave} value={chave}>
              {ROTULOS_SENTIDO[chave]}
            </option>
          ))}
        </select>
        <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={() => props.aoInverterSentido(corda.id)}>
          Inverter
        </button>
      </div>

      <ol className="lista-pontos-ancoragem corda-percurso" aria-label="Percurso da corda">
        {corda.percurso.map((ref, i) => (
          <li key={i}>{nomeDoPonto(cena, ref)}</li>
        ))}
      </ol>

      {props.aoDefinirFolga && (
        <details className="folgas-corda" open={Boolean(corda.folgas?.length)}>
          <summary>Folga de cada trecho</summary>
          <p className="dica">Corda a mais que a distância reta: o trecho fica frouxo, pendurado (uma catenária, uma folga de tensão).</p>
          {corda.percurso.slice(1).map((ref, i) => {
            const folga = folgaDoTrecho(corda, i)
            const nomeTrecho = `Trecho ${i + 1}: ${nomeDoPonto(cena, corda.percurso[i]!)} → ${nomeDoPonto(cena, ref)}`
            return (
              <label key={i} className="campo campo-folga">
                <span>
                  {nomeTrecho} · <strong>{formatarMetros(folga)}</strong>
                </span>
                <input
                  type="range"
                  min={0}
                  max={FOLGA_MAXIMA_TRECHO_M}
                  step={0.05}
                  value={folga}
                  aria-label={`Folga do trecho ${i + 1}`}
                  onChange={(e) => props.aoDefinirFolga!(corda.id, i, Number(e.target.value))}
                />
              </label>
            )
          })}
        </details>
      )}

      <EstudoAncoragemBasica cena={cena} corda={corda} />

      {soltas.length > 0 && (
        <p className="aviso-corda" role="alert">
          Ponta solta: {soltas.length === 1 ? 'um ponto desta corda não existe mais' : `${soltas.length} pontos desta corda não existem mais`}{' '}
          (o objeto mudou de forma). Apague a corda e desenhe de novo, ou restaure a medida do objeto.
        </p>
      )}

      <div className="acoes-linha">
        <button type="button" className="btn-acao-cad btn-acao-cad--secundario" onClick={() => props.aoSelecionarCorda(null)}>
          Fechar
        </button>
        <button type="button" className="btn-acao-cad btn-acao-cad--perigo" onClick={() => props.aoRemoverCorda(corda.id)}>
          Apagar corda
        </button>
      </div>
    </fieldset>
  )
}
